import {
    AnimationClip, Bone, BufferAttribute, BufferGeometry, ClampToEdgeWrapping,
    DoubleSide, Group, Matrix4, Mesh, MeshPhongMaterial, MeshStandardMaterial,
    QuaternionKeyframeTrack, RepeatWrapping, Skeleton, SkinnedMesh, TextureLoader, VectorKeyframeTrack,
} from "three";
import { TGALoader } from "three/examples/jsm/loaders/TGALoader.js";
import { parseLegacyFbx } from "./ufbx-parse.js";
import { resolveLegacyMaterialTextures } from "./ufbx-materials.js";

const clamp = value => Math.min(1, Math.max(0, value));

export async function loadLegacyFbx(buffer, manager, warnings, urls) {
    const data = await parseLegacyFbx(buffer);
    data.warnings.forEach(message => warnings.add(`FBX: ${message}`));
    const root = new Group(); root.name = "FBX"; root.userData.unitScaleFactor = 100;
    const geometries = [], materials = new Map(), textures = new Map();
    const boneIds = new Set(data.meshes.flatMap(mesh => mesh.skin?.bones.map(bone => bone.node) ?? []));
    const nodes = data.nodes.map((node, index) => {
        const object = node.bone || boneIds.has(index) ? new Bone() : new Group();
        object.name = node.name;
        object.position.fromArray(node.transform.p); object.quaternion.fromArray(node.transform.q); object.scale.fromArray(node.transform.s);
        object.visible = Boolean(node.visible);
        return object;
    });
    const loadTexture = (index, uvSets) => {
        const info = data.textures[index];
        if (!info) return null;
        if (!info.supported || (!info.file && !info.content.length)) {
            warnings.add(`${info.name || "Unnamed texture"}: procedural or layered texture could not be converted. Bake it to an image in the source application.`);
            return null;
        }
        let channel = uvSets.findIndex(uv => uv.name === info.uvSet);
        if (channel < 0) {
            channel = 0;
            if (info.uvSet && !["default", "UVMap"].includes(info.uvSet)) warnings.add(`${info.name}: UV set ${info.uvSet} was unavailable; the first UV set was used.`);
        }
        if (!uvSets.length || channel > 3) {
            warnings.add(`${info.name}: unsupported or missing UV coordinates; texture omitted.`);
            return null;
        }
        const key = `${index}:${channel}`;
        if (textures.has(key)) return textures.get(key);
        let url = info.file;
        if (info.content.length) {
            const extension = info.file.split(".").at(-1).toLowerCase();
            const type = { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", bmp: "image/bmp" }[extension];
            url = URL.createObjectURL(new Blob([info.content], { type: type ?? "application/octet-stream" })); urls.add(url);
        }
        const loader = /\.tga$/i.test(info.file) ? new TGALoader(manager) : new TextureLoader(manager);
        const texture = loader.load(url);
        texture.name = info.name; texture.channel = channel;
        texture.wrapS = info.wrap[0] ? ClampToEdgeWrapping : RepeatWrapping;
        texture.wrapT = info.wrap[1] ? ClampToEdgeWrapping : RepeatWrapping;
        const m = info.matrix;
        // Decompose the UV transform into the glTF texture transform convention.
        const sx = Math.hypot(m[0], m[4]), sy = Math.hypot(m[1], m[5]);
        const angle = sx ? Math.atan2(m[4], m[0]) : 0;
        const signedY = m[0]*m[5]-m[4]*m[1] < 0 ? -sy : sy;
        if (Math.abs(m[1] + signedY*Math.sin(angle)) > 1e-5 || Math.abs(m[5] - signedY*Math.cos(angle)) > 1e-5) {
            warnings.add(`${info.name}: UV shear cannot be represented in GLB; the texture transform was approximated.`);
        }
        texture.offset.set(m[12], m[13]); texture.repeat.set(sx, signedY); texture.rotation = angle;
        textures.set(key, texture);
        return texture;
    };
    const materialFor = (index, mesh) => {
        const key = `${index}:${Boolean(mesh.color)}:${mesh.uvs.map(uv => uv.name).join("|")}`;
        if (materials.has(key)) return materials.get(key);
        const info = data.materials[index];
        const material = info?.pbr ? new MeshStandardMaterial() : new MeshPhongMaterial();
        materials.set(key, material);
        material.vertexColors = Boolean(mesh.color);
        if (!info) { material.name = "Default material"; return material; }
        material.name = info.name;
        const mapping = resolveLegacyMaterialTextures(info, data.textures, warnings);
        material.color.fromArray(info.color.value).convertSRGBToLinear().multiplyScalar(info.colorFactor);
        if (mapping.diffuseReplacesColor) material.color.setRGB(1, 1, 1);
        material.emissive.fromArray(info.emission.value).convertSRGBToLinear().multiplyScalar(info.emissionFactor);
        material.opacity = clamp(info.opacityValue); material.transparent = material.opacity < 1;
        if (info.doubleSided) material.side = DoubleSide;
        if (info.pbr) { material.roughness = clamp(info.roughness.value[0]); material.metalness = clamp(info.metalness.value[0]); }
        else material.shininess = info.shininess;
        for (const [property, channel] of [["map", "color"], ["emissiveMap", "emission"], ["normalMap", "normal"], ["aoMap", "ao"], ["alphaMap", "opacity"]]) {
            material[property] = loadTexture(mapping.channels[channel], mesh.uvs);
            if (info[channel].disabledTexture) warnings.add(`${info.name}: a disabled ${channel} texture was not imported.`);
        }
        if (material.alphaMap) material.transparent = true;
        material.normalScale.setScalar(mapping.normalStrength);
        // PBR scalar maps require channel packing; do not silently interpret
        // arbitrary FBX image channels as glTF's G=roughness / B=metallic.
        if (info.unsupportedMaps || info.roughness.texture >= 0 || info.metalness.texture >= 0) warnings.add(`${info.name}: unsupported specular, bump, displacement or PBR scalar textures need baking; scalar material values were retained.`);
        return material;
    };
    try {
        data.nodes.forEach((node, index) => (node.parent < 0 ? root : nodes[node.parent]).add(nodes[index]));
        data.meshes.forEach(mesh => {
            const geometry = new BufferGeometry(); geometries.push(geometry);
            geometry.setAttribute("position", new BufferAttribute(mesh.position, 3));
            geometry.setAttribute("normal", new BufferAttribute(mesh.normal, 3));
            mesh.uvs.slice(0, 4).forEach((uv, i) => geometry.setAttribute(i ? `uv${i}` : "uv", new BufferAttribute(uv.values, 2)));
            if (mesh.color) geometry.setAttribute("color", new BufferAttribute(mesh.color, 4));
            if (mesh.skin) {
                geometry.setAttribute("skinIndex", new BufferAttribute(mesh.skin.joints, 4));
                geometry.setAttribute("skinWeight", new BufferAttribute(mesh.skin.weights, 4));
                if (mesh.skin.approximate) warnings.add("Skinning was approximated using the four strongest bone weights and linear blending.");
            }
            // Keep per-face material slots without losing assignments on an
            // instanced mesh that uses different node-level materials.
            for (let start = 0; start < mesh.slots.length;) {
                let end = start + 1;
                while (end < mesh.slots.length && mesh.slots[end] === mesh.slots[start]) end++;
                geometry.addGroup(start*3, (end-start)*3, mesh.slots[start]); start = end;
            }
            if (mesh.hasMorphs) warnings.add("Legacy FBX blend shapes are not exported; the base mesh and skeletal animation were retained.");
        });
        data.nodes.forEach((node, index) => {
            if (node.mesh < 0 || !data.meshes[node.mesh].position.length) return;
            const source = data.meshes[node.mesh], geometry = geometries[node.mesh];
            const maxSlot = geometry.groups.reduce((max, group) => Math.max(max, group.materialIndex), 0);
            const slots = Array.from({ length: maxSlot + 1 }, (_, i) => materialFor(node.materials[i] ?? -1, source));
            const mesh = source.skin ? new SkinnedMesh(geometry, slots) : new Mesh(geometry, slots);
            mesh.name = `${node.name || "Mesh"}_Geometry`; nodes[index].add(mesh);
            if (source.skin) {
                const skeleton = new Skeleton(source.skin.bones.map(bone => nodes[bone.node]), source.skin.bones.map(bone => new Matrix4().fromArray(bone.inverse)));
                mesh.bind(skeleton, new Matrix4());
            }
        });
        root.updateMatrixWorld(true);
        root.animations = data.animations.flatMap(animation => {
            if (animation.error) throw new Error(`Could not convert animation ${animation.name}: ${animation.error}`);
            const tracks = animation.nodes.flatMap(node => ["position", "quaternion", "scale"].flatMap(property => {
                const keys = node[property];
                if (!keys.times.length) return [];
                const Track = property === "quaternion" ? QuaternionKeyframeTrack : VectorKeyframeTrack;
                return [new Track(`${nodes[node.node].uuid}.${property}`, keys.times, keys.values)];
            }));
            return tracks.length ? [new AnimationClip(animation.name, -1, tracks)] : [];
        });
        return root;
    } catch (error) {
        geometries.forEach(geometry => geometry.dispose()); materials.forEach(material => material.dispose()); textures.forEach(texture => texture.dispose());
        root.traverse(node => node.skeleton?.dispose());
        throw error;
    }
}
